import { useEffect, useState } from "react"
import { HStack, Spinner, Stack, Table } from "@chakra-ui/react"
import { Text } from "@/components"
import { Deal } from "@/types"
import { formatDollar } from "@/utils"
import { getDmIrrReference, IrrReference } from "@/api/payments"

// Deal-manager-only panel in the view-as-client Payment Center (Bill, 2026-10-16): the IRR
// ledger's view of the deal, to read alongside the portal's own payment history. A deal paid
// outside the Payment Center has no history in it (Anduril 37163: $590,000 wired 7/20, portal
// ledger $0). The IRR is the source of truth for terms and payments, but this is its database
// copy, which can lag, miss rows or sit under the wrong deal, so the panel says so and never
// replaces the portal's numbers. Rendered only when a DM is viewing as the client; the route
// behind it answers deal-manager sessions only.

interface Props {
  currentDeal: Deal
}

const money = (v: number | null | undefined) => (v == null ? "—" : formatDollar(v))
const percent = (v: number | null | undefined) => (v == null ? "—" : `${(v * 100).toLocaleString(undefined, { maximumFractionDigits: 3 })}%`)
const near = (a: number | null | undefined, b: number | null | undefined, tol: number) =>
  a != null && b != null && Math.abs(a - b) <= tol

export const IrrReferencePanel = ({ currentDeal }: Props) => {
  const esoId = currentDeal.esoId
  const [data, setData] = useState<IrrReference | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!esoId) return
    let live = true
    setLoading(true)
    setError(null)
    getDmIrrReference(esoId)
      .then((d) => { if (live) setData(d) })
      .catch(() => { if (live) { setData(null); setError("The IRR reference is unavailable right now.") } })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [esoId])

  const irr = data?.deal ?? null
  const accepted = currentDeal.offers?.find((o) => o.isAccepted === 1 && o.isActive === 1)
  const offerTotal = accepted ? (accepted.exerciseCost ?? 0) + accepted.desiredLiquidity : null
  const offerMatches = accepted && irr
    ? near(offerTotal, irr.totalPayment, 1) && near(accepted.pp, irr.pp, 0.0005) && near(accepted.lp, irr.lp, 1)
    : null
  const paidOut = data ? data.cashFlows.filter((f) => (f.amount ?? 0) < 0).reduce((s, f) => s + Math.abs(f.amount ?? 0), 0) : 0
  const portalSent = currentDeal.sentAmount ?? 0

  return (
    <Stack p={5} borderWidth="1px" borderRadius="lg" borderColor="gray.300" bg="gray.50" gap={3}>
      <HStack justifyContent="space-between" flexWrap="wrap">
        <Text fontSize="md" fontWeight="bold" color="gray.800">IRR ledger (reference)</Text>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600">Deal managers only · the client never sees this</Text>
      </HStack>
      <Text fontSize="xs" color="gray.600">
        From the IRR workbook's database copy. The IRR is the source of truth for deal terms and payments, but this copy
        can lag, miss rows or sit under the wrong deal, so check it against the deal documents before relying on it.
      </Text>

      {loading ? (
        <HStack justifyContent="center" p={3}><Spinner size="sm" /></HStack>
      ) : error ? (
        <Text fontSize="sm" color="orange.700">{error}</Text>
      ) : data && !data.inIrr ? (
        <Text fontSize="sm" color="gray.700">This deal isn't in the IRR yet.</Text>
      ) : irr ? (
        <Stack gap={2}>
          <Text fontSize="sm" color="gray.800">
            {irr.closingDate ? `Closed ${irr.closingDate}` : "No closing date"}{irr.fund ? ` · ${irr.fund}` : ""}
            {" · "}ESO paid {money(irr.totalPayment)}
            {irr.initialPayment != null || irr.additionalPayment != null
              ? ` (initial ${money(irr.initialPayment)}, additional ${money(irr.additionalPayment)})` : ""}
            {" · "}PP {percent(irr.pp)} · LP {money(irr.lp)}
          </Text>
          <Text fontSize="sm" color="gray.800">
            Realized {money(irr.totalRealizations)} · Residual {money(irr.residualValue)}
            {irr.exitDate ? ` · Exit ${irr.exitDate}` : ""}{irr.asOf ? ` · IRR as of ${irr.asOf}` : ""}
          </Text>
          {offerMatches != null && (
            <Text fontSize="sm" color={offerMatches ? "green.700" : "red.600"}>
              {offerMatches
                ? "Matches the accepted offer on the portal."
                : `Differs from the accepted offer on the portal (${money(offerTotal)}, PP ${percent(accepted?.pp)}, LP ${money(accepted?.lp)}).`}
            </Text>
          )}
          {!near(portalSent, data && data.cashFlows.length ? paidOut : (irr.totalPayment ?? 0), 1) && (
            <Text fontSize="sm" color="orange.700">
              The portal ledger shows {money(portalSent)} sent; the IRR shows {money(data && data.cashFlows.length ? paidOut : irr.totalPayment)} paid out.
              Payments made outside the Payment Center show only here.
            </Text>
          )}
          {data && data.cashFlows.length > 0 ? (
            <Table.Root size="sm" mt={1}>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Date</Table.ColumnHeader>
                  <Table.ColumnHeader>Amount</Table.ColumnHeader>
                  <Table.ColumnHeader>IRR memo</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data.cashFlows.map((f, i) => (
                  <Table.Row key={`irr-${i}`}>
                    <Table.Cell whiteSpace="nowrap">{f.date ?? "—"}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap" color={(f.amount ?? 0) < 0 ? "red.600" : "green.600"}>
                      {f.amount == null ? "—" : `${f.amount < 0 ? "Paid out " : "Received "}${formatDollar(Math.abs(f.amount))}`}
                    </Table.Cell>
                    <Table.Cell fontSize="xs" color="gray.700">{f.memo ?? ""}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          ) : (
            <Text fontSize="sm" color="gray.700">No dated cash flows in the IRR for this deal, only the totals above.</Text>
          )}
        </Stack>
      ) : null}
    </Stack>
  )
}
